'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Clock } from 'lucide-react';
import { TextField } from './text-field';
const pad = (n) => String(n).padStart(2, '0');
/** 'HH:mm' → '06:30 PM' */
const toDisplay = (hhmm) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
    if (!m)
        return '';
    const h = Number(m[1]);
    if (h > 23 || Number(m[2]) > 59)
        return '';
    return `${pad(h % 12 || 12)}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
};
/** 12-hour parts → 'HH:mm' */
const to24 = (h12, min, mer) => `${pad((h12 % 12) + (mer === 'PM' ? 12 : 0))}:${pad(min)}`;
/**
 * The 12-hour typing + time-list logic, shared by TimeField and AdaptiveField (type="time") so
 * every time box behaves the same. Spread inputProps on a text <input>, put `picker` next to it
 * (inside a relative box), and show `invalidMessage` as the field error.
 */
export function useTimeTextInput({ value, onValueChange, disabled, onBlur, }) {
    const shown = toDisplay(value);
    const [text, setText] = React.useState(shown);
    React.useEffect(() => { setText(shown); }, [shown]);
    const pickerRef = React.useRef(null);
    const currentMeridiem = () => (/PM/.test(text) ? 'PM' : /AM/.test(text) ? 'AM' : /PM/.test(shown) ? 'PM' : 'AM');
    const value24 = (/^(\d{2}:\d{2})/.exec(value || '') || [])[1] || '';
    const emit = (hhmm) => { if (hhmm !== value24)
        onValueChange(hhmm); };
    const onChange = (e) => {
        const raw = e.target.value;
        let digits = raw.replace(/\D/g, '').slice(0, 4);
        // Backspace that only removed the space / letter of "AM": take a digit off instead, so it never gets stuck
        if (raw.length < text.length && digits === text.replace(/\D/g, ''))
            digits = digits.slice(0, -1);
        const typed = raw.replace(/[AP]M$/, '');
        const letter = /p/i.test(typed) ? 'PM' : /a/i.test(typed) ? 'AM' : null;
        let mer = letter ?? currentMeridiem();
        if (!digits) {
            setText('');
            if (value24)
                onValueChange('');
            return;
        }
        if (digits.length < 4) {
            setText(digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits);
            return;
        }
        let h = Number(digits.slice(0, 2));
        const min = Number(digits.slice(2));
        if (h > 12 && h <= 23) {
            mer = 'PM';
            h -= 12;
        } // typed as 24-hour (1830)
        else if (h === 0) {
            mer = 'AM';
            h = 12;
        } // 0030 → 12:30 AM
        if (h < 1 || h > 12 || min > 59) {
            setText(`${digits.slice(0, 2)}:${digits.slice(2)}`);
            return;
        }
        setText(`${pad(h)}:${pad(min)} ${mer}`);
        emit(to24(h, min, mer));
    };
    const openPicker = () => {
        const picker = pickerRef.current;
        if (!picker || disabled)
            return;
        try {
            picker.showPicker();
        }
        catch {
            picker.click();
        }
    };
    // A / P on a complete time flips it without retyping the digits; Alt+↓ opens the list
    const onKeyDown = (e) => {
        const m = /^(\d{2}):(\d{2}) (AM|PM)$/.exec(text);
        // (not with Ctrl / Alt / Cmd: Ctrl+A must still select all, Ctrl+P still print)
        if (m && /^[ap]$/i.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            const mer = e.key.toLowerCase() === 'p' ? 'PM' : 'AM';
            setText(`${m[1]}:${m[2]} ${mer}`);
            emit(to24(Number(m[1]), Number(m[2]), mer));
        }
        else if (e.key === 'ArrowDown' && e.altKey) {
            e.preventDefault();
            openPicker();
        }
    };
    // Leaving the box with a part-typed time shows the last good one again
    const handleBlur = () => {
        if (text && !/^\d{2}:\d{2} (AM|PM)$/.test(text))
            setText(shown);
        onBlur?.();
    };
    const handlePick = (e) => {
        const picked = e.target.value.slice(0, 5);
        setText(toDisplay(picked));
        emit(picked);
    };
    const partial = text.length === 5 && !/^\d{2}:\d{2} (AM|PM)$/.test(text);
    const invalidMessage = partial && (Number(text.slice(0, 2)) > 23 || Number(text.slice(3)) > 59) ? 'Not a valid time' : undefined;
    const picker = (_jsxs("span", { className: "relative flex items-center pointer-events-auto", children: [_jsx("button", { type: "button", tabIndex: -1, "aria-label": "Open time list", disabled: disabled, onClick: openPicker, className: "text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 disabled:cursor-not-allowed", children: _jsx(Clock, { className: "h-4 w-4" }) }), _jsx("input", { ref: pickerRef, type: "time", tabIndex: -1, "aria-hidden": "true", value: value24, onChange: handlePick, className: "absolute right-0 top-full w-px h-px opacity-0 pointer-events-none" })] }));
    return {
        inputProps: { value: text, onChange, onKeyDown, onBlur: handleBlur, inputMode: 'numeric', autoComplete: 'off' },
        invalidMessage,
        picker,
    };
}
export function TimeField({ label, error, helperText, required, disabled, value, onValueChange, className, inputClassName, size = 'md', placeholder = 'hh:mm AM', id, name, title, 'aria-label': ariaLabel, onBlur, }) {
    const time = useTimeTextInput({ value, onValueChange: (v) => onValueChange?.(v), disabled, onBlur });
    return (_jsx("div", { className: className, children: _jsx(TextField, { id: id, name: name, label: label, error: error || time.invalidMessage, helperText: helperText, required: required, disabled: disabled, className: inputClassName, size: size, title: title, "aria-label": ariaLabel, placeholder: placeholder, ...time.inputProps, endAdornment: time.picker }) }));
}
